import cn from "classnames";
import TariffSelectionForm from "components/modal/tariffSelectionForm";
import NewsPopover from "components/platformNews/popover";
import DropdownBtn from "components/shared/dropdown";
import ExternalBids from "components/shared/externalBids";
import TechSupportWidgets from "components/techSupportWidgets";
import { useUnreadItems } from "contexts/unreadItemsContext";
import { organizationDemoPeriodStatusesEnum, sourceTypesEnum } from "helpers/enums";
import { paths } from "helpers/paths";
import { noRolesOtherThan } from "helpers/roles";
import { formattedCount } from "helpers/system";
import { useDemoPeriod } from "hooks/useDemoPeriod";
import policy from "policies";
import Tooltip from "rc-tooltip";
import React, { useEffect, useRef, useState } from "react";
import { NavLink, withRouter } from "react-router-dom";
import { Popover } from "react-tiny-popover";
import { getCountOfAcceptedExternalForPlanning } from "sources/books/distributionBids";
import { deferTariffSelection } from "sources/profile/organization";
import { del as logoff } from "sources/session";
import connect from "./container";

function Header(props) {
    const [navOpened, setNavOpened] = useState(false);
    const [externalBidsOpened, setExternalBidsOpened] = useState(false);
    const [newsOpened, setNewsOpened] = useState(false);
    const [countOfAcceptedExternal, setCountOfAcceptedExternal] = useState(0);
    const { unreadItems } = useUnreadItems();
    const { toggleFullscreen, roles, profile, location, organization, history, jivoChatScriptUrl, getOrg } = props;
    const isBillingPath = location.pathname.startsWith(paths.profileBilling);
    const demoPeriodData = useDemoPeriod(organization, profile, getOrg, { suppressAutoShow: isBillingPath });

    const timer = useRef(null);
    const shouldDeferTariffSelectionOnClose = useRef(true);

    useEffect(() => {
        const {
            profile: { availableFeatureModules },
            roles: {
                isTruckingOrganizationViewer,
                isTruckingOrganizationExtendedViewer,
                isIntegrationManager,
                isTruckingOrganizationDispatcher,
            },
        } = props;
        if (
            availableFeatureModules?.includes(sourceTypesEnum.Forwarding) &&
            !isTruckingOrganizationViewer &&
            !isTruckingOrganizationExtendedViewer &&
            !isIntegrationManager &&
            !isTruckingOrganizationDispatcher
        ) {
            const updateCounter = () => {
                if (organization?.demoPeriod?.status === organizationDemoPeriodStatusesEnum.Expired) {
                    return;
                }
                getCountOfAcceptedExternalForPlanning().then(setCountOfAcceptedExternal);
            };

            updateCounter();

            timer.current = setInterval(updateCounter, 3 * 60 * 1000);
        }

        return () => {
            clearInterval(timer.current);
            document.removeEventListener("click", closeNav);
        };
    }, []);

    const handleLogout = (e) => {
        e.preventDefault();
        logoff();
    };

    const openSidebar = () => {
        props.toggleSidebar(true);
        setTimeout(() => document.addEventListener("click", closeSidebarHandle), 0);
    };

    const closeSidebarHandle = (e) => {
        if (shouldClose(e.target) && !e.target.classList.contains("checkbox")) {
            closeSidebar();
        } else {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    const closeSidebar = () => {
        props.toggleSidebar(false);
        document.removeEventListener("click", closeSidebarHandle);
    };

    const openNav = () => {
        setNavOpened(true);
        setTimeout(() => document.addEventListener("click", closeNav), 0);
    };

    const closeNav = () => {
        setNavOpened(false);
        document.removeEventListener("click", closeNav);
    };

    const shouldClose = (target) => {
        let current = target;
        for (let i = 0; i <= 10; i++) {
            if (!(current instanceof Element)) {
                return false;
            }
            const cList = current.classList;
            const isBtn = cList.contains("inline-btn--edit") || cList.contains("inline-btn--share");
            if (current.hasAttribute("href") || (isBtn && !cList.contains("inline-btn--disabled"))) {
                return true;
            }
            current = current.parentNode;
        }
        return false;
    };

    const renderDictionariesLink = () => {
        const { roles } = props;
        return policy("user", { user: { roles } }).isAdmin() ? (
            <li>
                <NavLink
                    activeClassName="header__link--active"
                    className="header__link header__link--dictionaries"
                    to={paths.dictionaries}
                    title="Словари"
                >
                    Словари
                </NavLink>
            </li>
        ) : null;
    };

    const cname = cn("header__collapse", { "header__collapse--opened": navOpened });

    const {
        isCargoOwnerAdmin,
        isCargoOwnerManager,
        isTruckingOrganizationAdmin,
        isTruckingOrganizationManager,
        isTruckingOrganizationOperator,
        isTruckingOrganizationDispatcher,
        isTruckingOrganizationExtendedViewer,
    } = roles;
    const dropdownItems = [
        {
            text: (
                <div className="d-flex">
                    <i className="icon-uEA02-avatar header__dropdown-icon" />
                    Профиль
                </div>
            ),

            action: () => history.push(paths.profileAccount),
            name: "header-profile",
            allow:
                isCargoOwnerAdmin ||
                isTruckingOrganizationAdmin ||
                isCargoOwnerManager ||
                isTruckingOrganizationManager ||
                isTruckingOrganizationOperator ||
                isTruckingOrganizationDispatcher ||
                isTruckingOrganizationExtendedViewer,
        },
        {
            text: (
                <div className="d-flex">
                    <i className="icon-uEA84-building header__dropdown-icon" />
                    Организация
                </div>
            ),

            action: () => history.push(paths.profileOrgData),
            name: "header-organization",
            allow: !noRolesOtherThan(roles, [
                "isTechSupport",
                "isAccountManager",
                "isDiagnostics",
                "isIntegrationManager",
            ]),
        },
        {
            text: (
                <div className="d-flex">
                    <i className="icon-uEA28-price header__dropdown-icon" />
                    Оплата
                </div>
            ),

            action: () => history.push(paths.profileBilling),
            name: "header-billing",
            allow: isTruckingOrganizationAdmin && organization.inn,
        },
    ].filter((item) => item.allow);

    useEffect(() => {
        if (demoPeriodData.showExpiringModal) {
            shouldDeferTariffSelectionOnClose.current = !demoPeriodData.hasDocument;
            props.modal({
                name: "default",
                onClose: () => {
                    demoPeriodData.setShowExpiringModal(false);
                    if (shouldDeferTariffSelectionOnClose.current) {
                        deferTariffSelection();
                    }
                    shouldDeferTariffSelectionOnClose.current = true;
                },
                CustomBody: TariffSelectionForm,
                windowMod: "wide",
                mod: "full",
                withoutHeader: true,
                bodyData: {
                    daysRemaining: demoPeriodData.daysRemaining,
                    hasDocument: demoPeriodData.hasDocument,
                    status: demoPeriodData.status,
                    onSkip: () => {
                        demoPeriodData.setShowExpiringModal(false);
                    },
                    onSubscribeSuccess: () => {
                        shouldDeferTariffSelectionOnClose.current = false;
                        demoPeriodData.setShowExpiringModal(false);
                    },
                    onOpenBillingDocuments: () => {
                        demoPeriodData.setShowExpiringModal(false);
                    },
                },
            });
        }
    }, [demoPeriodData.showExpiringModal, demoPeriodData.daysRemaining]);

    return (
        <header className={cn("page-layout__header header", props.className)}>
            <div className="header__body">
                {demoPeriodData.daysRemaining != null &&
                    demoPeriodData.status !== organizationDemoPeriodStatusesEnum.Completed && (
                        <Tooltip overlay={`Демо период: ${demoPeriodData.daysRemaining} дн.`}>
                            <div
                                onClick={() => demoPeriodData.setShowExpiringModal(true)}
                                className={cn(
                                    "align-self-center header__demo-period font-size-14 font-weight-semibold cursor-pointer mr-3 d-flex align-items-center justify-content-center",
                                    { "header__demo-period--expiring": demoPeriodData.daysRemaining <= 3 },
                                )}
                            >
                                {demoPeriodData.daysRemaining}
                            </div>
                        </Tooltip>
                    )}
                <a
                    className="font-size-14 text-muted cursor-pointer pr-2 font-weight-semibold d-sm-none d-md-block"
                    href="https://cargorun.skillspace.ru/school/courses"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                >
                    Обучение
                </a>
                {profile.availableFeatureModules?.includes(sourceTypesEnum.Forwarding) &&
                    !roles.isTruckingOrganizationViewer &&
                    !roles.isTruckingOrganizationExtendedViewer && (
                        <ExternalBids
                            organization={organization}
                            listOpened={externalBidsOpened}
                            setListOpened={setExternalBidsOpened}
                            setCountOfAcceptedExternal={setCountOfAcceptedExternal}
                            modal={props.modal}
                        />
                    )}
                <div className="header__burger header__burger--desktop" onClick={toggleFullscreen} />
                <div className="header__burger header__burger--mobile" onClick={openSidebar} />
                <div className="header__collapser" onClick={openNav} />
                {process.env.APP_ENV !== "demo" && !!organization.id && profile.email && (
                    <TechSupportWidgets
                        location={location}
                        profile={profile}
                        organization={organization}
                        jivoChatScriptUrl={jivoChatScriptUrl}
                        roles={roles}
                    />
                )}
                <div className={cname}>
                    <ul className="header__left-side no-bullet" />
                    <div className="d-flex">
                        <ul className="header__right-side w-100 no-bullet">
                            {process.env.APP_ENV !== "production" && (
                                <li>
                                    <Popover
                                        isOpen={newsOpened}
                                        positions="bottom"
                                        onClickOutside={() => setNewsOpened(false)}
                                        content={<NewsPopover closePopover={() => setNewsOpened(false)} />}
                                    >
                                        <div
                                            className={cn(
                                                "header__link header__link--platform-news d-md-inline-block cursor-pointer",
                                                {
                                                    "header__link--warning": unreadItems.news?.overallCount > 0,
                                                },
                                            )}
                                            onClick={() => setNewsOpened(!newsOpened)}
                                        >
                                            Новости платформы
                                            {unreadItems.news?.overallCount > 0 && (
                                                <div className="header__link-count header__link-count--in-corner header__link-count--warning">
                                                    {formattedCount(unreadItems.news?.overallCount)}
                                                </div>
                                            )}
                                        </div>
                                    </Popover>
                                </li>
                            )}
                            {profile.availableFeatureModules?.includes(sourceTypesEnum.Forwarding) &&
                                !roles.isTruckingOrganizationExtendedViewer && (
                                <li>
                                    <div
                                        className={cn(
                                            "header__link header__link--external-bids d-md-inline-block cursor-pointer",
                                            {
                                                "header__link--red": countOfAcceptedExternal > 0,
                                            },
                                        )}
                                        onClick={() => setExternalBidsOpened(!externalBidsOpened)}
                                    >
                                        Экспедиционные заказы
                                        {countOfAcceptedExternal > 0 && (
                                            <div className="header__link-count header__link-count--in-corner header__link-count--red">
                                                {formattedCount(countOfAcceptedExternal)}
                                            </div>
                                        )}
                                    </div>
                                </li>
                            )}
                            {renderDictionariesLink()}
                            {policy("user", { user: { roles } }).isNotGuest() && (
                                <li className="header__link--profile-wrap">
                                    <DropdownBtn
                                        name="header-dropd"
                                        wrapCname="header__link header__link--profile cursor-pointer overflow-visible"
                                        cname="header__dropdown ellipsis"
                                        text={profile.email}
                                        items={dropdownItems}
                                        contentCname="font-size-14"
                                    />
                                </li>
                            )}
                            <li>
                                <a
                                    className="header__link header__link--exit"
                                    data-test-id="header-logout"
                                    onClick={handleLogout}
                                    href="#"
                                    title="Выход"
                                >
                                    Выход
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </header>
    );
}

export default withRouter(connect(Header));
